import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getApiBase } from "../lib/apiBase";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const apiBase = useMemo(() => getApiBase(), []);

  const canSubmit = email.trim() && password && !isBusy;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      setStatus("");
      return;
    }

    setIsBusy(true);
    setError("");
    setStatus("Signing in...");

    try {
      const response = await fetch(`${apiBase}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
        }),
        credentials: "include",
      });

      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || "Login failed.");
      }

      setPassword("");
      setStatus("Signed in successfully.");
      navigate("/", { replace: true });
    } catch (err) {
      setStatus("");
      setError(err?.message || "Something went wrong.");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="login">
      <div className="login-head">
        <p className="eyebrow">Welcome back</p>
        <h2>Log in</h2>
        <p className="subtle">
          Use your email and password to access your account.
        </p>
      </div>

      <form className="form" onSubmit={handleSubmit}>
        <label className="field">
          <span>Email address</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </label>
        <label className="field">
          <span>Password</span>
          <div className="password-field">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Your password"
              autoComplete="current-password"
            />
            <button
              type="button"
              className="toggle-button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <svg
                className="eye-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M2.5 12s3.6-6.5 9.5-6.5S21.5 12 21.5 12 17.9 18.5 12 18.5 2.5 12 2.5 12Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                {showPassword ? (
                  <path
                    d="M4 20L20 4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                ) : null}
              </svg>
            </button>
          </div>
        </label>
        <button type="submit" disabled={!canSubmit}>
          {isBusy ? "Signing in..." : "Log in"}
        </button>
      </form>

      {status ? <div className="status">{status}</div> : null}
      {error ? <div className="error">{error}</div> : null}

      <div className="link-row">
        <Link className="link" to="/reset">
          Forgot your password?
        </Link>
        <Link className="link" to="/signup">
          Create an account
        </Link>
      </div>
    </div>
  );
}

export default Login;
